/**
 * Copies text to the clipboard, falling back to a hidden textarea when the async Clipboard API is
 * unavailable.
 *
 * Browsers only expose `navigator.clipboard` in a secure context. HTTPS and `localhost` qualify, but the
 * admin app is also served over plain HTTP on a named host (`admin.lvh.me`), where the API is absent and
 * calling it throws.
 *
 * @param {string} textToCopy - The text to place on the clipboard.
 * @returns {Promise<boolean>} Whether the copy succeeded.
 */
export const copyTextToClipboard = async (textToCopy) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    } catch {
      // Permission denied or a transient failure; the textarea fallback below still works.
    }
  }

  const stagingTextarea = document.createElement("textarea");
  stagingTextarea.value = textToCopy;
  stagingTextarea.setAttribute("readonly", "");
  stagingTextarea.style.position = "fixed";
  stagingTextarea.style.top = "-9999px";
  document.body.appendChild(stagingTextarea);

  try {
    stagingTextarea.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(stagingTextarea);
  }
};
